import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';
import OrganisationModal from '../../components/modals/OrganisationModal.js';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const OrganisationsPage = () => {
  const { currentUser } = useOutletContext();

  // Data state
  const [organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Selection state
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrganisation, setEditingOrganisation] = useState(null);
  
  // Categories modal state
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTiers, setFilterTiers] = useState(['Starter', 'Business', 'Pro']); // All selected by default
  const [filterStatuses, setFilterStatuses] = useState(['Active', 'Inactive']); // All selected by default
  const [filterFromMonth, setFilterFromMonth] = useState('');
  const [filterToMonth, setFilterToMonth] = useState('');
  
  // Subscription state filters
  const [filterIncompleteUpgrade, setFilterIncompleteUpgrade] = useState(false);
  const [filterPendingDowngrade, setFilterPendingDowngrade] = useState(false);
  const [filterGracePeriod, setFilterGracePeriod] = useState(false);
  const [filterFlashUpgrade, setFilterFlashUpgrade] = useState(false);
  const [filterPendingDeletion, setFilterPendingDeletion] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState('registrationDate');
  const [sortDirection, setSortDirection] = useState('desc'); // desc = newest first
  
  // Custom modal states for replacing browser dialogs
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });
  
  // Loading states for flash upgrade toggles
  const [flashUpgradeLoading, setFlashUpgradeLoading] = useState({}); // { orgId: true/false }
  const [bulkFlashUpgradeLoading, setBulkFlashUpgradeLoading] = useState(false);
  
  // Audit Log modal state
  const [auditLogModal, setAuditLogModal] = useState({
    isOpen: false,
    organisation: null,
    logs: [],
    loading: false,
    error: null
  });

  // Bulk status notification modal state
  const [bulkNotifyModal, setBulkNotifyModal] = useState({
    isOpen: false,
    statusChanges: [],
    sending: false
  });
  
  // Delete notification modal state
  const [deleteNotifyModal, setDeleteNotifyModal] = useState({
    isOpen: false,
    org: null,
    isBulk: false,
    orgsToDelete: [],
    deleting: false
  });
  const [immediateDeletePassword, setImmediateDeletePassword] = useState('');
  const [immediateDeleteError, setImmediateDeleteError] = useState('');
  const [immediateDeleting, setImmediateDeleting] = useState(false);

  // Fetch organisations from backend
  useEffect(() => {
    fetchOrganisations();
  }, []);

  const fetchOrganisations = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching organisations from:', `${API}/super-admin/managed-organisations`);
      console.log('Auth token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      
      const response = await axios.get(`${API}/super-admin/managed-organisations`);
      console.log('Organisations API response:', response.data);
      console.log('Number of organisations:', response.data?.length);
      console.log('Response status:', response.status);
      console.log('Full response:', response);
      
      if (Array.isArray(response.data)) {
        setOrganisations(response.data);
        
        // Set default date range if not already set
        if (response.data.length > 0 && !filterFromMonth && !filterToMonth) {
          // Find earliest registration date
          const dates = response.data.map(org => new Date(org.registrationDate));
          const earliestDate = new Date(Math.min(...dates));
          const earliestDateStr = earliestDate.toISOString().split('T')[0]; // YYYY-MM-DD
          
          // Get current date
          const now = new Date();
          const currentDateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
          
          console.log('Setting default date range:', { 
            from: earliestDateStr, 
            to: currentDateStr 
          });
          
          setFilterFromMonth(earliestDateStr);
          setFilterToMonth(currentDateStr);
        } else {
          console.log('Date defaults not set. Current values:', {
            filterFromMonth,
            filterToMonth,
            dataLength: response.data.length
          });
        }
      } else {
        console.error('Response is not an array:', response.data);
        setOrganisations([]);
      }
    } catch (error) {
      console.error('Error fetching organisations:', error);
      console.error('Error response:', error.response?.data);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: `Failed to load organisations: ${error.response?.data?.detail || error.message}`
      });
      setOrganisations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch business categories
  const fetchBusinessCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/business-categories`);
      setBusinessCategories(response.data);
    } catch (error) {
      console.error('Error fetching business categories:', error);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: 'Failed to load business categories'
      });
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleOpenCategoriesModal = () => {
    fetchBusinessCategories();
    setIsCategoriesModalOpen(true);
  };

  const handleCloseCategoriesModal = () => {
    setIsCategoriesModalOpen(false);
    setEditingCategory(null);
    setNewCategoryName('');
    setNewCategoryDescription('');
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      await axios.post(`${API}/super-admin/business-categories`, {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim()
      });
      setNewCategoryName('');
      setNewCategoryDescription('');
      fetchBusinessCategories();
      setSuccessModal({
        isOpen: true,
        title: 'Category Created',
        message: `Category "${newCategoryName}" has been created successfully.`
      });
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Create Failed',
        message: error.response?.data?.detail || 'Failed to create category'
      });
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editingCategory?.name?.trim()) return;
    
    try {
      await axios.put(`${API}/super-admin/business-categories/${categoryId}`, {
        name: editingCategory.name.trim(),
        description: editingCategory.description?.trim() || ''
      });
      setEditingCategory(null);
      fetchBusinessCategories();
      setSuccessModal({
        isOpen: true,
        title: 'Category Updated',
        message: 'Category has been updated successfully.'
      });
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: error.response?.data?.detail || 'Failed to update category'
      });
    }
  };

  const handleDeleteCategory = async (category) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        try {
          await axios.delete(`${API}/super-admin/business-categories/${category.id}`);
          fetchBusinessCategories();
          setSuccessModal({
            isOpen: true,
            title: 'Category Deleted',
            message: `Category "${category.name}" has been deleted.`
          });
        } catch (error) {
          setErrorModal({
            isOpen: true,
            title: 'Delete Failed',
            message: error.response?.data?.detail || 'Failed to delete category'
          });
        }
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'GRACE_PERIOD':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'GRACE_EXPIRED':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'CANCELLED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatSubscriptionStatus = (status) => {
    if (!status) return 'N/A';
    return status.replace(/_/g, ' ');
  };

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'pro':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'business':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'starter':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Filter and sort organisations
  const filteredOrganisations = useMemo(() => {
    console.log('Filtering with:', { 
      searchTerm, 
      filterTiers, 
      filterStatuses,
      filterFromMonth, 
      filterToMonth,
      filterIncompleteUpgrade,
      filterPendingDowngrade,
      filterGracePeriod,
      filterFlashUpgrade,
      filterPendingDeletion,
      sortField,
      sortDirection,
      orgCount: organisations.length 
    });
    
    const filtered = organisations.filter(org => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.organisation_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.id.toLowerCase().includes(searchTerm.toLowerCase());

      // Tier filter - must be in selected tiers (case-insensitive)
      const matchesTier = filterTiers.length === 0 || 
        filterTiers.some(tier => tier.toLowerCase() === (org.effective_tier || org.subscriptionTier || '').toLowerCase());

      // Status filter - must be in selected statuses (case-insensitive)
      const matchesStatus = filterStatuses.length === 0 || 
        filterStatuses.some(status => status.toLowerCase() === (org.status || '').toLowerCase());

      // Date range filter - comparing full dates
      let matchesDateRange = true;
      if (filterFromMonth || filterToMonth) {
        const orgDateStr = org.registrationDate; // Already in YYYY-MM-DD format
        
        if (filterFromMonth && orgDateStr < filterFromMonth) {
          matchesDateRange = false;
        }
        if (filterToMonth && orgDateStr > filterToMonth) {
          matchesDateRange = false;
        }
      }
      
      // Subscription state filters (these are additive - show if ANY checked filter matches)
      let matchesSubscriptionState = true;
      const hasAnySubscriptionFilter = filterIncompleteUpgrade || filterPendingDowngrade || filterGracePeriod || filterFlashUpgrade || filterPendingDeletion;
      
      if (hasAnySubscriptionFilter) {
        const hasIncompleteUpgrade = org.mid_subscription_upgrade_status === true;
        const hasPendingDowngrade = org.downgrade_pending === true;
        const hasGracePeriod = org.grace_period_end && new Date(org.grace_period_end) > new Date();
        const hasFlashUpgrade = org.flash_upgrade === true;
        const hasPendingDeletion = org.pending_deletion === true;
        
        matchesSubscriptionState = (
          (filterIncompleteUpgrade && hasIncompleteUpgrade) ||
          (filterPendingDowngrade && hasPendingDowngrade) ||
          (filterGracePeriod && hasGracePeriod) ||
          (filterFlashUpgrade && hasFlashUpgrade) ||
          (filterPendingDeletion && hasPendingDeletion)
        );
      }

      return matchesSearch && matchesTier && matchesStatus && matchesDateRange && matchesSubscriptionState;
    });
    
    // Sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'id':
          aValue = a.organisation_id || a.id;
          bValue = b.organisation_id || b.id;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'subscription_status':
          aValue = a.subscription_status || '';
          bValue = b.subscription_status || '';
          break;
        case 'location':
          aValue = (a.city || '').toLowerCase();
          bValue = (b.city || '').toLowerCase();
          break;
        case 'subscriptionTier':
          aValue = a.effective_tier || a.subscriptionTier;
          bValue = b.effective_tier || b.subscriptionTier;
          break;
        case 'registrationDate':
          aValue = a.registrationDate;
          bValue = b.registrationDate;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    console.log('Filtered and sorted results:', sorted.length);
    return sorted;
  }, [organisations, searchTerm, filterTiers, filterStatuses, filterFromMonth, filterToMonth, filterIncompleteUpgrade, filterPendingDowngrade, filterGracePeriod, filterFlashUpgrade, filterPendingDeletion, sortField, sortDirection]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrgs(filteredOrganisations.map(org => org.id));
    } else {
      setSelectedOrgs([]);
    }
  };

  const handleSelectOrg = (orgId) => {
    setSelectedOrgs(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };

  const isAllSelected = filteredOrganisations.length > 0 && selectedOrgs.length === filteredOrganisations.length;
  const isSomeSelected = selectedOrgs.length > 0 && selectedOrgs.length < filteredOrganisations.length;

  // Action handlers
  const handleEdit = (org) => {
    setEditingOrganisation(org);
    setIsModalOpen(true);
  };

  const handleAddOrganisation = () => {
    setEditingOrganisation(null);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    fetchOrganisations();
  };

  const handleDeleteSingle = async (org) => {
    setConfirmModal({
      isOpen: true,
      title: 'Schedule Deletion',
      message: `⚠️ Schedule "${org.name}" for deletion?\n\n• Organisation will be set to Inactive immediately\n• Permanent deletion will occur in 30 days\n• Admin will receive email reminders at 10, 3, and 1 day(s) remaining\n• You can cancel this action anytime within 30 days`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        // Show notification choice modal
        setDeleteNotifyModal({
          isOpen: true,
          org: org,
          isBulk: false,
          orgsToDelete: [],
          deleting: false
        });
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  // Handle immediate deletion with password verification
  const handleImmediateDelete = async () => {
    if (!immediateDeletePassword) {
      setImmediateDeleteError('Password is required');
      return;
    }
    
    const { org, isBulk, orgsToDelete } = deleteNotifyModal;
    setImmediateDeleting(true);
    setImmediateDeleteError('');
    
    try {
      if (isBulk) {
        const orgIds = orgsToDelete.map(o => o.id);
        const response = await axios.post(`${API}/super-admin/managed-organisations/immediate-delete`, {
          org_ids: orgIds,
          admin_password: immediateDeletePassword
        });

        const bulkErrors = response.data.errors || [];
        const gcsPartial = response.data.gcs_partial_failures || [];
        let bulkMsg = `✅ ${response.data.deleted_count} organisation(s) permanently deleted.`;
        if (bulkErrors.length > 0) {
          bulkMsg += `\n\n⚠️ Issues during deletion (${bulkErrors.length}):\n${bulkErrors.slice(0, 5).join('\n')}${bulkErrors.length > 5 ? `\n…and ${bulkErrors.length - 5} more` : ''}`;
        }
        if (gcsPartial.length > 0) {
          const totalFailed = gcsPartial.reduce((s, x) => s + (x.gcs_error_count || 0), 0);
          bulkMsg += `\n\n⚠️ ${totalFailed} cloud-storage file(s) across ${gcsPartial.length} organisation(s) could not be deleted and may need manual cleanup.`;
        }

        setSuccessModal({
          isOpen: true,
          title: 'Immediate Deletion Complete',
          message: bulkMsg
        });
        setSelectedOrgs([]);
      } else {
        const response = await axios.post(`${API}/super-admin/managed-organisations/${org.id}/immediate-delete`, {
          admin_password: immediateDeletePassword
        });

        // Surface partial-failure details (e.g. GCS files that couldn't be deleted).
        const gcsErrors = response.data.deletion_result?.breakdown?.gcs_errors || [];
        const gcsDeleted = response.data.deletion_result?.breakdown?.gcs_files || 0;
        let detailMsg = `✅ "${org.name}" has been permanently deleted.`;
        if (gcsErrors.length > 0) {
          detailMsg += `\n\n⚠️ ${gcsErrors.length} file(s) in cloud storage could not be deleted (${gcsDeleted} succeeded). These may need manual cleanup.`;
        }

        setSuccessModal({
          isOpen: true,
          title: 'Immediate Deletion Complete',
          message: detailMsg
        });
      }
      
      await fetchOrganisations();
      setDeleteNotifyModal({ isOpen: false, org: null, isBulk: false, orgsToDelete: [], deleting: false });
      setImmediateDeletePassword('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete. Please try again.';
      setImmediateDeleteError(errorMessage);
    } finally {
      setImmediateDeleting(false);
    }
  };

  // Handle delete with notification choice
  const handleDeleteWithNotify = async (notify) => {
    const { org, isBulk, orgsToDelete } = deleteNotifyModal;
    setDeleteNotifyModal(prev => ({ ...prev, deleting: true }));
    
    try {
      if (isBulk) {
        // Bulk delete with notify option
        const orgIds = orgsToDelete.map(o => o.id);
        const url = `${API}/super-admin/managed-organisations/bulk-delete?notify_admins=${notify}`;
        
        const response = await axios.post(url, orgIds);
        
        let message = `✅ ${response.data.scheduled_count} organisation(s) scheduled for deletion`;
        message += `\n\n📅 Permanent deletion on: ${new Date(response.data.deletion_scheduled_at).toLocaleDateString()}`;
        
        if (response.data.already_pending > 0) {
          message += `\n\n⚠️ ${response.data.already_pending} organisation(s) were already pending deletion`;
        }
        
        if (notify && response.data.notifications) {
          const { sent, failed } = response.data.notifications;
          if (sent > 0) {
            message += `\n\n📧 ${sent} notification email(s) sent`;
          }
          if (failed > 0) {
            message += `\n⚠️ ${failed} notification(s) could not be sent (no valid admin email)`;
          }
        }
        
        setSuccessModal({
          isOpen: true,
          title: 'Deletion Scheduled',
          message: message
        });
        setSelectedOrgs([]);
      } else {
        // Single delete with notify option
        const url = `${API}/super-admin/managed-organisations/${org.id}?notify_admin=${notify}`;
        const response = await axios.delete(url);
        
        let message = `✅ "${org.name}" scheduled for deletion`;
        message += `\n\n📅 Permanent deletion on: ${new Date(response.data.deletion_scheduled_at).toLocaleDateString()}`;
        message += `\n⏱️ ${response.data.days_until_deletion} days remaining`;
        
        if (notify && response.data.notification_sent) {
          message += `\n\n📧 Notification email sent to ${response.data.notified_email}`;
        } else if (notify && !response.data.notification_sent) {
          message += `\n\n⚠️ Could not send notification (no valid admin email)`;
        }
        
        setSuccessModal({
          isOpen: true,
          title: 'Deletion Scheduled',
          message: message
        });
      }
      
      await fetchOrganisations();
    } catch (error) {
      console.error('Error scheduling organisation for deletion:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to schedule deletion. Please try again.';
      setErrorModal({
        isOpen: true,
        title: 'Scheduling Failed',
        message: `❌ ${errorMessage}`
      });
    } finally {
      setDeleteNotifyModal({ isOpen: false, org: null, isBulk: false, orgsToDelete: [], deleting: false });
    }
  };

  // Handle cancel deletion for a single organisation
  const handleCancelDeletion = async (org) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Scheduled Deletion',
      message: `Are you sure you want to cancel the scheduled deletion of "${org.name}"?\n\nThe organisation will be restored to its previous status and the deletion will be cancelled.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        try {
          const response = await axios.post(
            `${API}/super-admin/managed-organisations/${org.id}/cancel-deletion?notify_admin=true`
          );
          
          let message = `✅ Deletion cancelled for "${org.name}"`;
          message += `\n\nOrganisation has been restored to "${response.data.restored_status}" status.`;
          
          if (response.data.notification_sent) {
            message += `\n\n📧 Notification sent to ${response.data.notified_email}`;
          }
          
          setSuccessModal({
            isOpen: true,
            title: 'Deletion Cancelled',
            message: message
          });
          
          await fetchOrganisations();
        } catch (error) {
          console.error('Error cancelling deletion:', error);
          setErrorModal({
            isOpen: true,
            title: 'Cancel Failed',
            message: `❌ Failed to cancel deletion: ${error.response?.data?.detail || 'Unknown error'}`
          });
        }
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  const handleBroadcastSingle = (org) => {
    console.log('Broadcast to organisation:', org);
    // TODO: Implement broadcast functionality
  };

  // Fetch and show audit logs for an organisation
  const handleViewAuditLog = async (org) => {
    setAuditLogModal({
      isOpen: true,
      organisation: org,
      logs: [],
      loading: true,
      error: null
    });

    try {
      const response = await axios.get(`${API}/super-admin/organisations/${org.organisation_id}/audit-log`);
      setAuditLogModal(prev => ({
        ...prev,
        logs: response.data.logs || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setAuditLogModal(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load audit logs'
      }));
    }
  };

  const handleCloseAuditLogModal = () => {
    setAuditLogModal({
      isOpen: false,
      organisation: null,
      logs: [],
      loading: false,
      error: null
    });
  };

  // Format audit log change type for display
  const formatChangeType = (changeType) => {
    const typeMap = {
      'organisation_deactivated': { label: 'Deactivated', color: 'bg-red-100 text-red-800', icon: '🔒' },
      'organisation_reactivated': { label: 'Reactivated', color: 'bg-green-100 text-green-800', icon: '🔓' },
      'organisation_deletion_scheduled': { label: 'Deletion Scheduled', color: 'bg-red-100 text-red-800', icon: '🗑️' },
      'organisation_deletion_cancelled': { label: 'Deletion Cancelled', color: 'bg-green-100 text-green-800', icon: '✅' },
      'subscription_suspended': { label: 'Subscription Suspended', color: 'bg-orange-100 text-orange-800', icon: '⏸️' },
      'subscription_reactivated': { label: 'Subscription Resumed', color: 'bg-blue-100 text-blue-800', icon: '▶️' },
      'upgrade_initiated': { label: 'Upgrade Started', color: 'bg-purple-100 text-purple-800', icon: '⬆️' },
      'upgrade_completed': { label: 'Upgrade Completed', color: 'bg-purple-100 text-purple-800', icon: '✅' },
      'downgrade_initiated': { label: 'Downgrade Started', color: 'bg-amber-100 text-amber-800', icon: '⬇️' },
      'downgrade_completed': { label: 'Downgrade Completed', color: 'bg-amber-100 text-amber-800', icon: '✅' },
      'downgrade_cancelled': { label: 'Downgrade Cancelled', color: 'bg-green-100 text-green-800', icon: '↩️' },
      'flash_upgrade_enabled': { label: 'Flash Upgrade Enabled', color: 'bg-yellow-100 text-yellow-800', icon: '⚡' },
      'flash_upgrade_expired': { label: 'Flash Upgrade Expired', color: 'bg-gray-100 text-gray-800', icon: '⌛' },
      'flash_upgrade_cancelled': { label: 'Flash Upgrade Cancelled', color: 'bg-gray-100 text-gray-800', icon: '❌' },
      'subscription_created': { label: 'Subscription Created', color: 'bg-green-100 text-green-800', icon: '🎉' },
      'subscription_cancelled': { label: 'Subscription Cancelled', color: 'bg-red-100 text-red-800', icon: '🚫' },
      'reconciliation_status_fixed': { label: 'Status Reconciled', color: 'bg-blue-100 text-blue-800', icon: '🔧' },
      'reconciliation_plan_fixed': { label: 'Plan Reconciled', color: 'bg-blue-100 text-blue-800', icon: '🔧' },
    };
    return typeMap[changeType] || { label: changeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), color: 'bg-gray-100 text-gray-800', icon: '📝' };
  };

  const handleToggleSingleStatus = async (org) => {
    const newStatus = org.status === 'Active' ? 'Inactive' : 'Active';
    
    try {
      // Optimistic update
      setOrganisations(prev => 
        prev.map(o => o.id === org.id ? { ...o, status: newStatus } : o)
      );

      // API call to update status
      await axios.put(`${API}/super-admin/managed-organisations/${org.id}`, {
        status: newStatus
      });

      console.log(`✅ Status toggled: ${org.name} is now ${newStatus}`);
    } catch (error) {
      // Revert on error
      setOrganisations(prev => 
        prev.map(o => o.id === org.id ? { ...o, status: org.status } : o)
      );
      console.error('Error toggling status:', error);
      setErrorModal({
        isOpen: true,
        title: 'Status Update Failed',
        message: `❌ Failed to update status for ${org.name}. Please try again.`
      });
    }
  };


  // Helper function to detect if an organisation is ACTUALLY on Pro tier (via PayPal subscription)
  // This should NOT check effective_tier because that includes flash upgrades
  // We need to know the real subscription tier to determine if flash upgrade is applicable
  const isOrganisationProTier = (org) => {
    // Only check the actual stored/PayPal tier, NOT effective_tier
    // effective_tier includes flash upgrades which would incorrectly block the toggle
    const tierName = (org.subscriptionTier || '').toLowerCase().trim();
    const tierPackage = (org.subscription_package_id || '').toLowerCase().trim();
    
    // Check exact match on tier name (most reliable)
    if (tierName === 'pro') return true;
    
    // Check subscription_package_id patterns
    // Valid: "pro", "pro-tier-001", "pro_tier_monthly", "pro-annual"
    // Invalid: "support-pro-123", "nonpro-tier", "approved-tier"
    if (tierPackage === 'pro') return true;
    if (tierPackage.startsWith('pro-') || tierPackage.startsWith('pro_')) return true;
    
    return false;
  };

  const handleToggleSingleFlashUpgrade = async (org) => {
    const isCurrentlyEnabled = org.flash_upgrade;
    const isProTier = isOrganisationProTier(org);
    
    // Check if org is on Pro tier - flash upgrade not applicable
    if (isProTier && !isCurrentlyEnabled) {
      setErrorModal({
        isOpen: true,
        title: 'Flash Upgrade Not Available',
        message: `⚠️ ${org.name} is already on the Pro tier. Flash upgrade is only available for organisations on lower tiers.`
      });
      return;
    }
    
    // Prevent rapid clicking - check if already loading
    if (flashUpgradeLoading[org.id]) {
      return;
    }
    
    // Set loading state for this org
    setFlashUpgradeLoading(prev => ({ ...prev, [org.id]: true }));
    
    try {
      // Optimistic update
      setOrganisations(prev => 
        prev.map(o => o.id === org.id ? { ...o, flash_upgrade: !isCurrentlyEnabled } : o)
      );

      // Use the dedicated flash upgrade endpoints
      if (isCurrentlyEnabled) {
        // Disable flash upgrade
        await axios.post(`${API}/super-admin/flash-upgrades/disable`, {
          organisation_id: org.organisation_id
        });
        setSuccessModal({
          isOpen: true,
          title: 'Flash Upgrade Disabled',
          message: `✅ Flash upgrade has been disabled for ${org.name}. The organisation has been reverted to their original tier.`
        });
      } else {
        // Enable flash upgrade
        await axios.post(`${API}/super-admin/flash-upgrades/enable`, {
          organisation_id: org.organisation_id
        });
        setSuccessModal({
          isOpen: true,
          title: 'Flash Upgrade Enabled',
          message: `⚡ Flash upgrade has been enabled for ${org.name}. They now have temporary Pro tier access.`
        });
      }

      // Refresh data to get accurate state
      fetchOrganisations();
    } catch (error) {
      // Revert on error
      setOrganisations(prev => 
        prev.map(o => o.id === org.id ? { ...o, flash_upgrade: org.flash_upgrade } : o)
      );
      console.error('Error toggling flash upgrade:', error);
      const errorMessage = error.response?.data?.detail || 'An unexpected error occurred';
      setErrorModal({
        isOpen: true,
        title: 'Flash Upgrade Failed',
        message: `❌ ${errorMessage}`
      });
    } finally {
      // Clear loading state
      setFlashUpgradeLoading(prev => ({ ...prev, [org.id]: false }));
    }
  };


  // Bulk action handlers
  const handleBulkDelete = async () => {
    // Get the full org objects for the selected IDs (excluding those already pending deletion)
    const orgsToDelete = organisations.filter(org => selectedOrgs.includes(org.id) && !org.pending_deletion);
    const alreadyPending = organisations.filter(org => selectedOrgs.includes(org.id) && org.pending_deletion);
    
    if (orgsToDelete.length === 0) {
      setErrorModal({
        isOpen: true,
        title: 'No Eligible Organisations',
        message: `⚠️ All selected organisations are already scheduled for deletion.\n\nUse "Cancel Deletion" to restore them instead.`
      });
      return;
    }
    
    let message = `⚠️ Schedule ${orgsToDelete.length} organisation(s) for deletion?\n\n• Organisations will be set to Inactive immediately\n• Permanent deletion will occur in 30 days\n• Admins will receive email reminders\n• You can cancel this action anytime within 30 days`;
    
    if (alreadyPending.length > 0) {
      message += `\n\n⚠️ ${alreadyPending.length} organisation(s) are already pending deletion and will be skipped.`;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Schedule Bulk Deletion',
      message: message,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        // Show notification choice modal for bulk delete
        setDeleteNotifyModal({
          isOpen: true,
          org: null,
          isBulk: true,
          orgsToDelete: orgsToDelete,
          deleting: false
        });
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  const handleBulkBroadcast = () => {
    setErrorModal({
      isOpen: true,
      title: 'Feature Not Available',
      message: '📢 Broadcast feature will be implemented in a future update'
    });
    console.log('Broadcast to organisations:', selectedOrgs);
    // TODO: Implement bulk broadcast functionality
  };

  const handleFlashUpgrade = () => {
    // Prevent if already processing
    if (bulkFlashUpgradeLoading) return;
    
    // Get selected organisations that are eligible for flash upgrade (not on Pro tier and not already flash upgraded)
    const selectedOrgObjects = organisations.filter(org => selectedOrgs.includes(org.id));
    const eligibleOrgs = selectedOrgObjects.filter(org => !isOrganisationProTier(org) && !org.flash_upgrade);
    const proTierOrgs = selectedOrgObjects.filter(org => isOrganisationProTier(org));
    const alreadyUpgradedOrgs = selectedOrgObjects.filter(org => org.flash_upgrade);

    if (eligibleOrgs.length === 0) {
      let message = '⚠️ No eligible organisations selected for flash upgrade.\n\n';
      if (proTierOrgs.length > 0) {
        message += `• ${proTierOrgs.length} organisation(s) are already on Pro tier\n`;
      }
      if (alreadyUpgradedOrgs.length > 0) {
        message += `• ${alreadyUpgradedOrgs.length} organisation(s) already have flash upgrade enabled\n`;
      }
      setErrorModal({
        isOpen: true,
        title: 'No Eligible Organisations',
        message
      });
      return;
    }

    // Show confirmation modal
    setConfirmModal({
      isOpen: true,
      title: 'Enable Flash Upgrade',
      message: `⚡ You are about to enable Flash Upgrade for ${eligibleOrgs.length} organisation(s).\n\nThis will grant temporary Pro tier access.\n\n${proTierOrgs.length > 0 ? `Note: ${proTierOrgs.length} Pro tier organisation(s) will be skipped.\n` : ''}${alreadyUpgradedOrgs.length > 0 ? `Note: ${alreadyUpgradedOrgs.length} organisation(s) already have flash upgrade enabled and will be skipped.\n` : ''}\nProceed?`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        // Set bulk loading state
        setBulkFlashUpgradeLoading(true);
        
        let successCount = 0;
        let failCount = 0;
        
        for (const org of eligibleOrgs) {
          try {
            await axios.post(`${API}/super-admin/flash-upgrades/enable`, {
              organisation_id: org.organisation_id
            });
            successCount++;
          } catch (error) {
            console.error(`Failed to enable flash upgrade for ${org.name}:`, error);
            failCount++;
          }
        }
        
        // Clear bulk loading state
        setBulkFlashUpgradeLoading(false);
        
        // Refresh data
        fetchOrganisations();
        setSelectedOrgs([]);
        
        if (failCount === 0) {
          setSuccessModal({
            isOpen: true,
            title: 'Flash Upgrade Complete',
            message: `⚡ Successfully enabled flash upgrade for ${successCount} organisation(s).`
          });
        } else {
          setErrorModal({
            isOpen: true,
            title: 'Partial Success',
            message: `⚡ Flash upgrade enabled for ${successCount} organisation(s).\n❌ Failed for ${failCount} organisation(s).`
          });
        }
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  const handleToggleStatus = async () => {
    const activeCount = filteredOrganisations.filter(org => 
      selectedOrgs.includes(org.id) && org.status === 'Active'
    ).length;
    const inactiveCount = selectedOrgs.length - activeCount;

    setConfirmModal({
      isOpen: true,
      title: 'Toggle Organisation Status',
      message: `Toggle status for ${selectedOrgs.length} organisation(s)?\n\n• ${activeCount} Active → Inactive\n• ${inactiveCount} Inactive → Active\n\nClick Confirm to proceed.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        try {
          const response = await axios.post(
            `${API}/super-admin/managed-organisations/bulk-toggle-status`,
            selectedOrgs
          );
          
          // Check if we have status changes to notify about
          const statusChanges = response.data.status_changes || [];
          const validChanges = statusChanges.filter(c => c.admin_email);
          
          if (validChanges.length > 0) {
            // Show notification modal
            setBulkNotifyModal({
              isOpen: true,
              statusChanges: statusChanges,
              sending: false
            });
          } else {
            // No admins to notify, just show success
            setSuccessModal({
              isOpen: true,
              title: 'Status Updated',
              message: `✅ ${response.data.updated_count} organisation(s) status updated successfully`
            });
          }
          
          setSelectedOrgs([]);
          await fetchOrganisations();
        } catch (error) {
          console.error('Error toggling status:', error);
          setErrorModal({
            isOpen: true,
            title: 'Status Toggle Failed',
            message: '❌ Failed to toggle organisation status. Please try again.'
          });
        }
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterTiers(['Starter', 'Business', 'Pro']); // Reset to all selected
    setFilterStatuses(['Active', 'Inactive']); // Reset to all selected
    
    // Reset subscription state filters
    setFilterIncompleteUpgrade(false);
    setFilterPendingDowngrade(false);
    setFilterGracePeriod(false);
    setFilterFlashUpgrade(false);
    
    // Reset to default date range
    if (organisations.length > 0) {
      const dates = organisations.map(org => new Date(org.registrationDate));
      const earliestDate = new Date(Math.min(...dates));
      const earliestDateStr = earliestDate.toISOString().split('T')[0]; // YYYY-MM-DD
      
      const now = new Date();
      const currentDateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      
      setFilterFromMonth(earliestDateStr);
      setFilterToMonth(currentDateStr);
    }
  };

  // Handle sending bulk status notifications
  const handleSendBulkNotifications = async () => {
    setBulkNotifyModal(prev => ({ ...prev, sending: true }));
    
    try {
      const validChanges = bulkNotifyModal.statusChanges
        .filter(c => c.admin_email)
        .map(c => ({
          organisation_id: c.organisation_id,
          company_name: c.company_name,
          old_status: c.old_status,
          new_status: c.new_status,
          admin_email: c.admin_email,
          admin_name: c.admin_name || 'Admin'
        }));
      
      const response = await axios.post(
        `${API}/super-admin/managed-organisations/bulk-status-notify`,
        { status_changes: validChanges }
      );
      
      if (response.data.success) {
        setSuccessModal({
          isOpen: true,
          title: 'Notifications Sent',
          message: `✅ ${response.data.total_emails} notification email(s) are being sent in background.`
        });
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      setErrorModal({
        isOpen: true,
        title: 'Notification Failed',
        message: `❌ Failed to send notifications: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setBulkNotifyModal({ isOpen: false, statusChanges: [], sending: false });
    }
  };

  // Handle skipping bulk notifications
  const handleSkipBulkNotifications = () => {
    setBulkNotifyModal({ isOpen: false, statusChanges: [], sending: false });
    setSuccessModal({
      isOpen: true,
      title: 'Status Updated',
      message: `✅ Organisation status(es) updated successfully. No notifications sent.`
    });
  };

  const handleTierToggle = (tier) => {
    setFilterTiers(prev => {
      if (prev.includes(tier)) {
        return prev.filter(t => t !== tier);
      } else {
        return [...prev, tier];
      }
    });
  };

  const handleStatusToggle = (status) => {
    setFilterStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600 text-lg">Loading organisations...</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header - Matching Theme */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Organisations</h1>
              <p className="text-purple-200 text-sm">Manage all organisations in the system</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleOpenCategoriesModal}
              className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span>Categories</span>
            </button>
            <button 
              onClick={handleAddOrganisation}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Organisation</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Actions Section - Compressed */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-6">
        {/* Filters Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <h3 className="text-base font-semibold text-gray-900">Filters</h3>
            {filteredOrganisations.length !== organisations.length && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                {filteredOrganisations.length} of {organisations.length}
              </span>
            )}
          </div>
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors flex items-center space-x-1.5 text-xs"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Reset</span>
          </button>
        </div>

        {/* Filters Content - Compressed */}
        <div className="space-y-2.5">
          {/* Row 1: Search and Subscription Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Search Box */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or ID..."
                  className="w-full px-3 py-1.5 pl-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Subscription Tier Checkboxes */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subscription Tiers</label>
              <div className="flex items-center space-x-3 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                {['Starter', 'Business', 'Pro'].map(tier => (
                  <label key={tier} className="flex items-center space-x-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterTiers.includes(tier)}
                      onChange={() => handleTierToggle(tier)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="text-xs text-gray-700 font-medium whitespace-nowrap">{tier}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      tier === 'Pro' ? 'bg-purple-100 text-purple-800' :
                      tier === 'Business' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {organisations.filter(org => (org.effective_tier || org.subscriptionTier) === tier).length}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2: Status, From Date, To Date */}
          <div className="grid grid-cols-3 gap-3">
            {/* Status Filter - Checkboxes */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <div className="flex items-center space-x-3 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                {['Active', 'Inactive'].map(status => (
                  <label key={status} className="flex items-center space-x-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterStatuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="text-xs text-gray-700 font-medium whitespace-nowrap">{status}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {organisations.filter(org => org.status === status).length}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Registered From */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Registered From</label>
              <input
                type="date"
                value={filterFromMonth}
                onChange={(e) => setFilterFromMonth(e.target.value)}
                max={filterToMonth || new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* To */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
              <input
                type="date"
                value={filterToMonth}
                onChange={(e) => setFilterToMonth(e.target.value)}
                min={filterFromMonth}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Row 3: Subscription State Filters */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subscription States</label>
            <div className="flex flex-wrap items-center gap-3 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              {/* Incomplete Upgrade */}
              <label className="flex items-center space-x-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterIncompleteUpgrade}
                  onChange={() => setFilterIncompleteUpgrade(!filterIncompleteUpgrade)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-xs text-gray-700 font-medium whitespace-nowrap">Incomplete Upgrade</span>
                <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  {organisations.filter(org => org.mid_subscription_upgrade_status === true).length}
                </span>
              </label>
              
              {/* Pending Downgrade */}
              <label className="flex items-center space-x-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterPendingDowngrade}
                  onChange={() => setFilterPendingDowngrade(!filterPendingDowngrade)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                />
                <span className="text-xs text-gray-700 font-medium whitespace-nowrap">Pending Downgrade</span>
                <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  {organisations.filter(org => org.downgrade_pending === true).length}
                </span>
              </label>
              
              {/* Grace Period */}
              <label className="flex items-center space-x-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterGracePeriod}
                  onChange={() => setFilterGracePeriod(!filterGracePeriod)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                />
                <span className="text-xs text-gray-700 font-medium whitespace-nowrap">Grace Period</span>
                <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                  {organisations.filter(org => org.grace_period_end && new Date(org.grace_period_end) > new Date()).length}
                </span>
              </label>
              
              {/* Flash Upgrade */}
              <label className="flex items-center space-x-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterFlashUpgrade}
                  onChange={() => setFilterFlashUpgrade(!filterFlashUpgrade)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                />
                <span className="text-xs text-gray-700 font-medium whitespace-nowrap">Flash Upgrade</span>
                <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  {organisations.filter(org => org.flash_upgrade === true).length}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Bulk Actions
            {selectedOrgs.length > 0 && (
              <span className="ml-2 text-xs font-normal text-purple-600">
                ({selectedOrgs.length} selected)
              </span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleBulkDelete}
              disabled={selectedOrgs.length === 0}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600 flex items-center space-x-1.5 text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>

            <button
              onClick={handleBulkBroadcast}
              disabled={selectedOrgs.length === 0}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 flex items-center space-x-1.5 text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              <span>Broadcast</span>
            </button>

            <button
              onClick={handleFlashUpgrade}
              disabled={selectedOrgs.length === 0 || bulkFlashUpgradeLoading}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600 flex items-center space-x-1.5 text-xs"
            >
              {bulkFlashUpgradeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Flash Upgrade</span>
                </>
              )}
            </button>

            <button
              onClick={handleToggleStatus}
              disabled={selectedOrgs.length === 0}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-700 flex items-center space-x-1.5 text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span>Make Active / Inactive</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pending Deletion Banner - After Filters */}
      {organisations.filter(org => org.pending_deletion === true).length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500 rounded-full">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-red-800 text-lg">
                {organisations.filter(org => org.pending_deletion === true).length} Organisation{organisations.filter(org => org.pending_deletion === true).length !== 1 ? 's' : ''} Pending Deletion
              </p>
              <p className="text-sm text-red-600">
                These organisations will be permanently deleted after their 30-day grace period
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-red-700">Show Pending Deletions</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFilterPendingDeletion(!filterPendingDeletion);
              }}
              className={`relative inline-flex items-center h-7 w-14 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                filterPendingDeletion ? 'bg-red-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block w-5 h-5 transform bg-white rounded-full shadow-md transition-transform ${
                  filterPendingDeletion ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Organisations Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900">
                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = isSomeSelected;
                      }
                    }}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </th>
                <th 
                  onClick={() => handleSort('id')}
                  className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Organisation ID</span>
                    {sortField === 'id' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('name')}
                  className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Organisation ({filteredOrganisations.length})</span>
                    {sortField === 'name' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('subscription_status')}
                  className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Sub Status</span>
                    {sortField === 'subscription_status' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider"
                >
                  <span>Flash Upgrade</span>
                </th>
                <th 
                  onClick={() => handleSort('location')}
                  className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Location (City)</span>
                    {sortField === 'location' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('subscriptionTier')}
                  className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Subscription Tier</span>
                    {sortField === 'subscriptionTier' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('registrationDate')}
                  className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Registration Date</span>
                    {sortField === 'registrationDate' && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        {sortDirection === 'asc' ? (
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrganisations.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-lg font-medium text-gray-500">
                      {searchTerm || filterTiers.length < 3 || filterFromMonth || filterToMonth
                        ? 'No organisations match your filters'
                        : 'No organisations found'}
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      {searchTerm || filterTiers.length < 3 || filterFromMonth || filterToMonth
                        ? 'Try adjusting your filters'
                        : 'Get started by creating your first organisation'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredOrganisations.map((org, index) => (
                  <tr 
                    key={org.id} 
                    onClick={() => handleEdit(org)}
                    className={`cursor-pointer transition-all ${index % 2 === 0 ? 'bg-white hover:bg-purple-50' : 'bg-gray-50 hover:bg-purple-100'}`}
                  >
                    <td className="px-6 py-4 w-12" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedOrgs.includes(org.id)}
                        onChange={() => handleSelectOrg(org.id)}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border border-gray-300">
                        {org.organisation_id || org.id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900">{org.name}</div>
                        {/* Subscription State Flags */}
                        <div className="flex items-center gap-1">
                          {/* Incomplete Upgrade Flag */}
                          {org.mid_subscription_upgrade_status && (
                            <span 
                              className="relative group"
                              title={`Incomplete upgrade to ${org.mid_subscription_upgrade_target_tier || 'higher tier'}`}
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 cursor-help">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                <div className="font-semibold text-blue-300">Incomplete Upgrade</div>
                                <div>Target: {org.mid_subscription_upgrade_target_tier || 'N/A'}</div>
                                {org.mid_subscription_upgrade_initiated_at && (
                                  <div>
                                    Expires: {(() => {
                                      const initiated = new Date(org.mid_subscription_upgrade_initiated_at);
                                      const expiresAt = new Date(initiated.getTime() + (24 * 60 * 60 * 1000));
                                      const hoursRemaining = Math.max(0, (expiresAt - new Date()) / (1000 * 60 * 60));
                                      return `${Math.round(hoursRemaining)}h remaining`;
                                    })()}
                                  </div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                          
                          {/* Pending Downgrade Flag */}
                          {org.downgrade_pending && (
                            <span 
                              className="relative group"
                              title={`Pending downgrade to ${org.downgrade_new_plan || 'lower tier'}`}
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-600 cursor-help">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                <div className="font-semibold text-amber-300">Pending Downgrade</div>
                                <div>From: {org.downgrade_previous_plan || 'N/A'}</div>
                                <div>To: {org.downgrade_new_plan || 'N/A'}</div>
                                {org.downgrade_effective_date && (
                                  <div>Effective: {new Date(org.downgrade_effective_date).toLocaleDateString()}</div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                          
                          {/* Grace Period Flag */}
                          {org.grace_period_end && new Date(org.grace_period_end) > new Date() && (
                            <span 
                              className="relative group"
                              title="In grace period"
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-600 cursor-help">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                <div className="font-semibold text-orange-300">Grace Period</div>
                                <div>Ends: {new Date(org.grace_period_end).toLocaleDateString()}</div>
                                {org.grace_period_days && (
                                  <div>Duration: {org.grace_period_days} days</div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                          
                          {/* Flash Upgrade Flag */}
                          {org.flash_upgrade && (
                            <span 
                              className="relative group"
                              title="Flash upgrade active"
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-600 cursor-help">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                <div className="font-semibold text-purple-300">Flash Upgrade Active</div>
                                <div>Temporary Pro access</div>
                                {org.flash_upgrade_expires_at && (
                                  <div>Expires: {new Date(org.flash_upgrade_expires_at).toLocaleDateString()}</div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                          
                          {/* Pending Deletion Flag */}
                          {org.pending_deletion && (
                            <span 
                              className="relative group"
                              title="Scheduled for deletion"
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 cursor-help animate-pulse">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                <div className="font-semibold text-red-300">Pending Deletion</div>
                                {org.deletion_scheduled_at && (
                                  <>
                                    <div>Deletes: {new Date(org.deletion_scheduled_at).toLocaleDateString()}</div>
                                    <div>
                                      ({Math.ceil((new Date(org.deletion_scheduled_at) - new Date()) / (1000 * 60 * 60 * 24))} days remaining)
                                    </div>
                                  </>
                                )}
                                {org.deletion_reason && (
                                  <div className="mt-1 text-gray-300">Reason: {org.deletion_reason}</div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleSingleStatus(org)}
                        disabled={org.pending_deletion}
                        className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${org.pending_deletion ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{
                          backgroundColor: org.status === 'Active' ? '#10B981' : '#EF4444'
                        }}
                        title={org.pending_deletion ? 'Cannot change status - pending deletion' : `Click to make ${org.status === 'Active' ? 'Inactive' : 'Active'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            org.status === 'Active' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <div className={`mt-1 text-xs font-medium ${
                        org.status === 'Active' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {org.status}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${getSubscriptionStatusColor(org.subscription_status)}`}>
                        {formatSubscriptionStatus(org.subscription_status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const isProTier = isOrganisationProTier(org);
                        const isLoading = flashUpgradeLoading[org.id];
                        
                        if (isProTier) {
                          return (
                            <>
                              <div 
                                className="relative inline-flex items-center h-6 w-11 rounded-full bg-gray-200 cursor-not-allowed opacity-50"
                                title="Flash Upgrade not available for Pro tier"
                              >
                                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
                              </div>
                              <div className="mt-1 text-xs font-medium text-gray-400">
                                N/A (Pro)
                              </div>
                            </>
                          );
                        }
                        
                        return (
                          <>
                            <button
                              onClick={() => handleToggleSingleFlashUpgrade(org)}
                              disabled={isLoading}
                              className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                isLoading ? 'bg-gray-300 cursor-wait' : org.flash_upgrade ? 'bg-yellow-500' : 'bg-gray-300'
                              } ${isLoading ? 'opacity-60' : ''}`}
                              title={isLoading ? 'Processing...' : `Click to ${org.flash_upgrade ? 'disable' : 'enable'} Flash Upgrade`}
                            >
                              {isLoading ? (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              ) : (
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    org.flash_upgrade ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              )}
                            </button>
                            <div className={`mt-1 text-xs font-medium ${
                              isLoading ? 'text-gray-400' : org.flash_upgrade ? 'text-yellow-700' : 'text-gray-500'
                            }`}>
                              {isLoading ? 'Processing...' : org.flash_upgrade ? '⚡ Enabled' : 'Disabled'}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {org.city || '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {/* Actual tier user has access to (accounting for pending downgrade) */}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getTierColor(
                          org.downgrade_pending && org.downgrade_previous_plan_name 
                            ? org.downgrade_previous_plan_name 
                            : org.subscriptionTier
                        )}`}>
                          {org.downgrade_pending && org.downgrade_previous_plan_name 
                            ? org.downgrade_previous_plan_name 
                            : (org.subscriptionTier || 'Unknown')}
                        </span>
                        {/* State indicators */}
                        <div className="flex items-center gap-1">
                          {org.flash_upgrade && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300" title="Flash upgrade active - temporary Pro access">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                              </svg>
                              Pro
                            </span>
                          )}
                          {org.downgrade_pending && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300" title={`Pending downgrade to ${org.downgrade_new_plan_name || 'lower tier'}`}>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              → {org.downgrade_new_plan_name || 'Lower'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-700">
                      {new Date(org.registrationDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-2">
                        {/* Edit Button */}
                        <button
                          onClick={() => handleEdit(org)}
                          className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 hover:text-purple-700 transition-all duration-200"
                          title="Edit organisation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                        {/* Audit Log Button */}
                        <button
                          onClick={() => handleViewAuditLog(org)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-700 transition-all duration-200"
                          title="View audit log"
                          data-testid={`audit-log-btn-${org.id}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>

                        {/* Broadcast Button */}
                        <button
                          onClick={() => handleBroadcastSingle(org)}
                          className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-all duration-200"
                          title="Send broadcast to organisation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                          </svg>
                        </button>

                        {/* Delete / Cancel Deletion Button */}
                        {org.pending_deletion ? (
                          <button
                            onClick={() => handleCancelDeletion(org)}
                            className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 hover:text-green-700 transition-all duration-200"
                            title="Cancel scheduled deletion"
                            data-testid={`cancel-deletion-btn-${org.id}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeleteSingle(org)}
                            className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 transition-all duration-200"
                            title="Schedule for deletion"
                            data-testid={`delete-btn-${org.id}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Organisation Modal */}
      <OrganisationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingOrganisation(null);
        }}
        organisation={editingOrganisation}
        onSuccess={handleModalSuccess}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={confirmModal.onClose}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        title={successModal.title}
        message={successModal.message}
        onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
      />

      {/* Bulk Status Notification Modal */}
      {bulkNotifyModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Status Updated Successfully!</h3>
                  <p className="text-sm opacity-90">Would you like to notify affected admins?</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                The following organisations had their status changed. You can notify their admins via email.
              </p>

              {/* Changes Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Organisation</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Admin Email</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Status Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkNotifyModal.statusChanges.map((change, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-700">{change.company_name}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {change.admin_email || <span className="text-red-500 italic">No email</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 ${change.new_status === 'Active' ? 'text-green-600' : 'text-red-600'}`}>
                            <span className="line-through text-gray-400">{change.old_status}</span>
                            <span>→</span>
                            <span className="font-medium">{change.new_status}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>{bulkNotifyModal.statusChanges.filter(c => c.admin_email).length}</strong> admin(s) will receive email notifications.
                  {bulkNotifyModal.statusChanges.filter(c => !c.admin_email).length > 0 && (
                    <span className="text-amber-600 ml-2">
                      ({bulkNotifyModal.statusChanges.filter(c => !c.admin_email).length} organisation(s) have no admin email)
                    </span>
                  )}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSkipBulkNotifications}
                  disabled={bulkNotifyModal.sending}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50"
                >
                  Skip Notifications
                </button>
                <button
                  onClick={handleSendBulkNotifications}
                  disabled={bulkNotifyModal.sending || bulkNotifyModal.statusChanges.filter(c => c.admin_email).length === 0}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {bulkNotifyModal.sending ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Notifications
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Notification Modal */}
      {deleteNotifyModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className={`bg-white rounded-xl shadow-2xl mx-4 overflow-hidden ${deleteNotifyModal.isBulk ? 'max-w-2xl w-full' : 'max-w-md w-full'}`}>
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">
                    {deleteNotifyModal.isBulk 
                      ? `Schedule ${deleteNotifyModal.orgsToDelete.length} Organisation(s) for Deletion?`
                      : 'Schedule Organisation for Deletion?'
                    }
                  </h3>
                  <p className="text-sm opacity-90">30-day grace period with email notifications</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Single org info */}
              {!deleteNotifyModal.isBulk && deleteNotifyModal.org && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Organisation:</strong> {deleteNotifyModal.org.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Admin Email:</strong> {deleteNotifyModal.org.adminEmail || <span className="text-amber-600 italic">Not available</span>}
                  </p>
                </div>
              )}

              {/* Bulk delete org list */}
              {deleteNotifyModal.isBulk && deleteNotifyModal.orgsToDelete.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    The following organisations will be scheduled for deletion:
                  </p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Organisation</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Admin Email</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deleteNotifyModal.orgsToDelete.map((org, idx) => (
                          <tr key={org.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 font-medium text-gray-700">{org.name}</td>
                            <td className="px-3 py-2 text-gray-600">
                              {org.adminEmail || <span className="text-amber-500 italic">No email</span>}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                org.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {org.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Summary */}
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>{deleteNotifyModal.orgsToDelete.filter(o => o.adminEmail).length}</strong> admin(s) will receive deletion notification emails.
                      {deleteNotifyModal.orgsToDelete.filter(o => !o.adminEmail).length > 0 && (
                        <span className="text-amber-600 ml-2">
                          ({deleteNotifyModal.orgsToDelete.filter(o => !o.adminEmail).length} organisation(s) have no admin email)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600 mb-4">
                {deleteNotifyModal.isBulk
                  ? 'Would you like to send notification emails to the admin users? They will receive emails about the scheduled deletion and reminder emails at 10, 3, and 1 day(s) before permanent deletion.'
                  : 'Would you like to send a notification email to the admin user? They will receive emails about the scheduled deletion and reminder emails at 10, 3, and 1 day(s) before permanent deletion.'
                }
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>30-Day Grace Period:</strong> Organisations will be set to Inactive immediately and permanently deleted after 30 days. You can cancel this action anytime before permanent deletion.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleDeleteWithNotify(true)}
                  disabled={deleteNotifyModal.deleting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleteNotifyModal.deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Notify Admin{deleteNotifyModal.isBulk ? 's' : ''} & Schedule Deletion
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteWithNotify(false)}
                  disabled={deleteNotifyModal.deleting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg hover:from-red-600 hover:to-rose-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleteNotifyModal.deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Schedule Without Notifying
                    </>
                  )}
                </button>

                {/* Immediate Delete section */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Immediate deletion (no grace period):</p>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={immediateDeletePassword}
                      onChange={(e) => { setImmediateDeletePassword(e.target.value); setImmediateDeleteError(''); }}
                      placeholder="Super admin password"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      data-testid="immediate-delete-password"
                    />
                    <button
                      onClick={handleImmediateDelete}
                      disabled={immediateDeleting || !immediateDeletePassword}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
                      data-testid="immediate-delete-btn"
                    >
                      {immediateDeleting ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                      Delete Now
                    </button>
                  </div>
                  {immediateDeleteError && (
                    <p className="text-xs text-red-600 mt-1">{immediateDeleteError}</p>
                  )}
                </div>

                <button
                  onClick={() => { setDeleteNotifyModal({ isOpen: false, org: null, isBulk: false, orgsToDelete: [], deleting: false }); setImmediateDeletePassword(''); setImmediateDeleteError(''); }}
                  disabled={deleteNotifyModal.deleting || immediateDeleting}
                  className="w-full px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Modal */}
      {isCategoriesModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Business Categories</h2>
                    <p className="text-purple-200 text-sm">Manage platform-wide business categories</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseCategoriesModal}
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Add New Category */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Add New Category</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Category name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleCreateCategory}
                    disabled={!newCategoryName.trim()}
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Category</span>
                  </button>
                </div>
              </div>

              {/* Categories List */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Existing Categories ({businessCategories.length})
                </h3>
                
                {categoriesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : businessCategories.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No categories found. Add your first category above.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...businessCategories].sort((a, b) => a.name.localeCompare(b.name)).map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                      >
                        {editingCategory?.id === category.id ? (
                          <div className="flex-1 mr-3 space-y-2">
                            <input
                              type="text"
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder="Category name"
                            />
                            <input
                              type="text"
                              value={editingCategory.description || ''}
                              onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder="Description (optional)"
                            />
                          </div>
                        ) : (
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{category.name}</div>
                            {category.description && (
                              <div className="text-sm text-gray-500">{category.description}</div>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-2">
                          {editingCategory?.id === category.id ? (
                            <>
                              <button
                                onClick={() => handleUpdateCategory(category.id)}
                                className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                                title="Save"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setEditingCategory(null)}
                                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                                title="Cancel"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingCategory({ ...category })}
                                className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category)}
                                className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <button
                onClick={handleCloseCategoriesModal}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {auditLogModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Audit Log</h3>
                    <p className="text-sm opacity-90">{auditLogModal.organisation?.name || 'Organisation'}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseAuditLogModal}
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {auditLogModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin h-8 w-8 text-slate-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-500">Loading audit logs...</span>
                  </div>
                </div>
              ) : auditLogModal.error ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-red-600 font-medium">{auditLogModal.error}</p>
                </div>
              ) : auditLogModal.logs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 font-medium">No audit logs found</p>
                  <p className="text-gray-400 text-sm mt-1">Activity will appear here when changes are made</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogModal.logs.map((log, index) => {
                    const typeInfo = formatChangeType(log.change_type);
                    return (
                      <div 
                        key={log._id || index} 
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <span className="text-xl">{typeInfo.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                                  {typeInfo.label}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(log.timestamp).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              {log.details && Object.keys(log.details).length > 0 && (
                                <div className="mt-2 text-sm text-gray-600">
                                  {log.details.deactivated_by && (
                                    <p><span className="font-medium">By:</span> {log.details.deactivated_by}</p>
                                  )}
                                  {log.details.reactivated_by && (
                                    <p><span className="font-medium">By:</span> {log.details.reactivated_by}</p>
                                  )}
                                  {log.details.reason && (
                                    <p><span className="font-medium">Reason:</span> {log.details.reason}</p>
                                  )}
                                  {log.details.paypal_result && !log.details.paypal_result.skipped && (
                                    <p><span className="font-medium">PayPal:</span> {log.details.paypal_result.success ? '✅ Success' : '❌ Failed'}</p>
                                  )}
                                  {log.details.sessions_ended && (log.details.sessions_ended.clock_sessions_ended > 0 || log.details.sessions_ended.user_sessions_invalidated > 0) && (
                                    <p><span className="font-medium">Sessions Ended:</span> {log.details.sessions_ended.clock_sessions_ended} clock, {log.details.sessions_ended.user_sessions_invalidated} login</p>
                                  )}
                                  {log.details.bulk_action && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 mt-1">Bulk Action</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
              <button
                onClick={handleCloseAuditLogModal}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationsPage;
